import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { withRouter } from 'react-router-dom';
import { multiply, subtract, propOr } from 'ramda';
import { Row, Col } from 'antd';

import {
  slugify, smoothScrollTo, smoothScroll, isSSR,
} from '../../helpers/view';
import { artistById } from '../../actions/artist';
import { setPageNotFound } from '../../actions/app';
import { releasesByArtist } from '../../actions/release';
import { tracksByArtist } from '../../actions/track';
import ReleaseCard from '../../components/ReleaseCard';
import { artistReleaseByTypeList, trackListByArtist } from '../../selectors';
import TrackItem from '../../components/TrackItem/TrackItem';
import { BuyTrackButton } from '../../components/BuyButton/BuyButton';
import Pagination, { getReleaseList } from '../../components/Pagination';
import { DownloadTrackButton } from '../../components/DownloadButton';
import { defaultPagination, releaseBlocks } from './common';
import SheetTitle from '../../components/SheetTitle';
import Error from '../../components/Error';
import LoadingContainer from '../../components/LoadingContainer';

import c from './Artist.less';

class ArtistContainer extends Component {
  static propTypes = {
    app: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    artistById: PropTypes.func.isRequired,
    releasesByArtist: PropTypes.func.isRequired,
    tracksByArtist: PropTypes.func.isRequired,
    loggedIn: PropTypes.bool.isRequired,
    releaseList: PropTypes.object.isRequired,
    artistList: PropTypes.object.isRequired,
    artistReleaseList: PropTypes.object.isRequired,
    artistTrackList: PropTypes.object.isRequired,
    setPageNotFound: PropTypes.func.isRequired,
  };

  constructor(...args) {
    super(...args);
    this.pagination = {};

    this.blockPages = releaseBlocks.reduce((acc, block) => {
      acc[block.id] = 1;
      return acc;
    }, {});

    releaseBlocks.forEach((releaseBlock) => {
      this.pagination[releaseBlock.id] = { ...defaultPagination };
    });
  }

  UNSAFE_componentWillMount() {
    if (isSSR()) {
      const { match: { params: { id, slug } }, artistList } = this.props;
      const artist = artistList.data[id];

      if (!artist) {
        this.props.staticContext.page.status = 404;
      } else {
        const correctSlug = slugify(artist.name);

        if (typeof slug === 'string' && slug !== correctSlug) {
          this.props.staticContext.page.status = 301;
          this.props.history.replace(`/artist/${slugify(artist.name, artist.id)}`);
        }
      }
    }
  }

  componentDidMount() {
    this._browserRenderedAction();

    smoothScroll();
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { match: { params: { id: currentId } } } = this.props;
    const { match: { params: { slug, id } }, artistList } = newProps;

    if (id !== currentId) {
      return this.loadArtistData(Number(id));
    }

    const artist = artistList.data[id];

    if (artist) {
      const correctSlug = slugify(artist.name);

      if (typeof slug === 'string' && slug !== correctSlug) {
        if (this.props.staticContext?.page) this.props.staticContext.page.status = 301;
        this.props.history.replace(`/artist/${slugify(artist.name, artist.id)}`);
      }
    } else if (artistList.loaded) {
      this.props.setPageNotFound();
    }

    return true;
  }

  handleLoadMore = (block) => () => {
    this.blockPages[block.id] += 1;
    this._loadArtistReleaseBlock(block);
  };

  scrollTo = (block) => (e) => {
    e.preventDefault();
    smoothScrollTo(`#${block}`);
  };

  _loadArtistReleaseBlock(block) {
    const { match: { params: { id: artistId } } } = this.props;

    this.props.releasesByArtist(artistId, block.types, {
      limit: defaultPagination.maxCols,
      offset: multiply(defaultPagination.maxCols, subtract(propOr(1, block.id, this.blockPages), 1)),
    });
  }

  loadArtistData(artistId) {
    return this.props.artistById(artistId)
      .then(() => {
        releaseBlocks.forEach((releaseBlock) => this._loadArtistReleaseBlock(releaseBlock));

        this.props.tracksByArtist(artistId, undefined, undefined, true);
      })
      .catch(({ body }) => {
        if (body.errorCode === 'ENTITY_NOT_FOUND') {
          this.props.setPageNotFound();
        }
      });
  }

  _browserRenderedAction() {
    if (this.props.app.renderedComponent === 'Artist') return;

    const { match: { params: { id: artistId } } } = this.props;
    this.loadArtistData(Number(artistId));
  }

  renderSummary() {
    const { artistReleaseList, artistTrackList } = this.props;

    const summaryList = [];

    if (artistTrackList.total > 0) {
      summaryList.push(
        <li key="tracks">
          <a href="#tracks" onClick={this.scrollTo('tracks')}>
            <strong>{artistTrackList.total}</strong>
            {' '}
            <span>Tracks</span>
          </a>
        </li>
      );
    }

    if (artistReleaseList.total.Album > 0) {
      summaryList.push(
        <li key="albums">
          <a href="#albums" onClick={this.scrollTo('albums')}>
            <strong>{artistReleaseList.total.Album}</strong>
            {' '}
            <span>Albums</span>
          </a>
        </li>
      );
    }

    if (artistReleaseList.total['Single,Remix'] > 0) {
      summaryList.push(
        <li key="singles">
          <a href="#singles" onClick={this.scrollTo('singles')}>
            <strong>{artistReleaseList.total['Single,Remix']}</strong>
            <span>Singles & Remixes</span>
          </a>
        </li>
      );
    }

    if (artistReleaseList.total['Artist_Compilation,Live'] > 0) {
      summaryList.push(
        <li key="compilations">
          <a href="#compilations" onClick={this.scrollTo('compilations')}>
            <strong>{artistReleaseList.total['Artist_Compilation,Live']}</strong>
            <span>Compilations & Concerts</span>
          </a>
        </li>
      );
    }

    return summaryList.length > 0 ? (
      <ul className={c.summary}>
        {
          summaryList.reduce((result, element, index, array) => {
            result.push(element);
            if (index < array.length - 1) {
              result.push(<li key={`sep-${index}`} className={c.sep} />);
            }
            return result;
          }, [])
        }
      </ul>
    ) : null;
  }

  render() {
    const {
      artistList,
      match: { params: { id: artistId, slug } },
      artistReleaseList,
      releaseList,
      artistTrackList,
    } = this.props;

    const artist = artistList.data[Number(artistId)];

    if (!artistList.loading && artistList.error) {
      return <Error errorCode="404" />;
    }

    if (!artist) {
      return <LoadingContainer />;
    }

    return (
      <div className={c.container}>
        <Helmet defer={false}>
          <title>{`Buy and Download ${artist.name} Music at Mp3Caprice`}</title>
          <meta
            name="description"
            content={`Discover, buy and download high quality mp3 music by ${artist.name} at Mp3Caprice.`}
          />
          <meta property="og:type" content="profile" />
          <meta property="og:title" content={artist.name} />
          <meta property="og:image" content={`${artist.coverUrl}/230.jpg`} />
          <meta property="mc:dp" content={`/catalog/artist/${slug}-${artistId}`} />
          <meta property="mc:page" content="Artist" />
        </Helmet>
        <div className={c.artistInfo}>
          <div className={c.artistNameWrap}>
            <div className={c.photoCover}>
              <img alt={`${artist.name} Music Discography`} src={`${artist.coverUrl}/230.jpg`} width="100%" />
            </div>
            <h1 className={c.artistName}>{artist.name}</h1>
          </div>
          {this.renderSummary()}
        </div>
        <SheetTitle title="Top tracks" id="tracks" />
        <div className={c.trackList}>
          {
            artistTrackList.items.map((track) => (
              <TrackItem
                className={c.trackItem}
                key={track.id}
                track={track}
                showRelease
                actions={
                  track.downloadUrl ? (
                    <DownloadTrackButton
                      className={c.downloadTrackButton}
                      url={track.downloadUrl}
                      fileSize={track.fileSize}
                      wasDownloaded={!!track.lastDownloadedTime}
                    />
                  ) : (
                    <BuyTrackButton track={track} alt={`Buy ${track.name} mp3 song by ${artist.name}`} />
                  )
                }
              />
            ))
          }
        </div>
        {
          releaseBlocks.map((releaseBlock) => {
            const totalReleases = artistReleaseList.total[releaseBlock.types.join(',')] || 0;
            let releaseIdList = artistReleaseList.ids[releaseBlock.types.join(',')] || [];

            releaseIdList = releaseIdList.slice(0, this.blockPages[releaseBlock.id] * defaultPagination.maxCols);

            return totalReleases > 0
              ? (
                <div className={classNames(c.infoBlock, c.artistReleases)} key={releaseBlock.id} id={releaseBlock.id}>
                  <SheetTitle title={releaseBlock.title} />
                  <Row
                    gutter={{
                      xs: 10, sm: 16, md: 20, lg: 20,
                    }}
                    className={c.releasesBlock}
                  >
                    {
                      getReleaseList(releaseIdList, releaseList, artistList)
                        .map((release) => (
                          <Col xs={12} sm={6} md={6} lg={4} key={release.id}>
                            <ReleaseCard
                              showArtist={false}
                              type="light"
                              release={release}
                            />
                          </Col>
                        ))
                    }
                  </Row>
                  <Pagination
                    pageSize={defaultPagination.maxCols}
                    total={totalReleases}
                    currentPage={this.blockPages[releaseBlock.id]}
                    isLoading={releaseList.loading}
                    onLoadMore={this.handleLoadMore(releaseBlock)}
                    showPages={false}
                    showMoreTitle="Show More"
                  />
                </div>
              ) : null;
          })
        }
      </div>
    );
  }
}

const mapStateToProps = (store, props) => ({
  app: store.app,
  loggedIn: store.user.loggedIn,
  releaseList: store.releases.list,
  artistList: store.artistList,
  artistReleaseList: artistReleaseByTypeList(store, props),
  artistTrackList: trackListByArtist(store, props),
});

const mapDispatchToProps = {
  artistById,
  releasesByArtist,
  tracksByArtist,
  setPageNotFound,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ArtistContainer));
